import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import { useGlobalLayout } from '$contexts/global-layout';
import { HeaderLink, HeaderDrawer, LocaleSelector } from './index.js';
import { HeaderLogo, IconChevronSprite } from '$partials/icons/index.js';
import { useLocale } from '$contexts/locale.jsx';
import MobileNavigation from '$partials/global/mobile-navigation.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const HeaderLanding = ({
    menu,
    stylingWhite,
    setStylingWhite = () => { }
}) => {
    // hooks
    const {
        formatURI,
        translate: t,
    } = useLocale();

    const { navigationPhase, headerOpen } = useGlobalLayout();

    // Scroll behavior state
    const [headerClasses, setHeaderClasses] = useState('opacity-1 text-white translate-y-0 z-50');
    const [buttonClass, setButtonClass] = useState('button--primary');
    const [textColorClass, setTextColorClass] = useState('');
    const [logoBgStyle, setLogoBgStyle] = useState('transparent');
    const scrollPosition = useRef(0);
    const headerRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            if (navigationPhase === 'LANDINGHERO') {
                setHeaderClasses('opacity-1 text-white translate-y-0 z-10');
                setButtonClass('button--primary');
                setTextColorClass('');
                setLogoBgStyle('transparent');
                setStylingWhite(true);
            } else if (navigationPhase === 'PEEKABOO') {
                if (window.scrollY < scrollPosition.current) {
                    // Scrolling UP
                    setHeaderClasses('text-charcoal bg-sand translate-y-0 z-50');
                    setButtonClass('button--header-up bg-black');
                    setLogoBgStyle('black');
                    setStylingWhite(false);
                } else {
                    // Scrolling DOWN
                    setHeaderClasses('text-charcoal bg-sand -translate-y-100 z-50');
                    setButtonClass('button--header-up bg-black');
                    setLogoBgStyle('black');
                }
            } else if (navigationPhase === 'ANIMATE') {
                setHeaderClasses('hidden z-10');
            }
            scrollPosition.current = window.scrollY;
        };

        const headerRefNode = headerRef.current;
        if (headerOpen) {
            setHeaderClasses('bg-sand text-black translate-y-0 z-10 box-shadow-navigation z-50');
            setLogoBgStyle('white');
            disableBodyScroll(headerRefNode);
            setStylingWhite(false);
        }

        if (!headerOpen) {
            setHeaderClasses('bg-sand text-black translate-y-0 z-10');
            clearAllBodyScrollLocks();
            handleScroll();
        }

        // Throttle scroll events for better performance
        let ticking = false;
        const onScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [navigationPhase, headerOpen]);

    // misc
    if (!menu) { return null; }

    // Format links helper function
    const createMenuItem = (item, title, index) => {
        const collection = item?.dropdown?.length > 0 ? item?.dropdown : item?.single;
        const dropdown = collection?.length > 0;

        return {
            collection,
            dropdown,
            index,
            title
        };
    };

    const menuItems = [
        createMenuItem({ dropdown: menu?.link1DropdownCollection?.items, single: menu?.link1 }, menu?.link1Title, 1),
        createMenuItem({ dropdown: menu?.link2DropdownCollection?.items, single: menu?.link2 }, menu?.link2Title, 2),
        createMenuItem({ dropdown: menu?.link3DropdownCollection?.items, single: menu?.link3 }, menu?.link3Title, 3),
        createMenuItem({ dropdown: menu?.link4DropdownCollection?.items, single: menu?.link4 }, menu?.link4Title, 4),
    ];

    return (
        <>
            <div
                className={`${headerClasses} fixed w-full top-0 transition-transform duration-200`}
                ref={headerRef}>
                <div className='layout-container h-[40px] md:h-[60px] flex items-center'>
                    <header className='w-full relative px-10 sm:px-5 md:px-10 flex items-center justify-between type-style-7 font-semibold'>
                        <a
                            className='block'
                            href={formatURI('/')}>
                            <HeaderLogo bgStyle={logoBgStyle} />
                        </a>

                        <div className={`md:hidden flex-grow ${textColorClass} flex-grow text-right flex items-center justify-end`}>
                            <MobileNavigation menu={menuItems} />
                        </div>

                        <nav className={`navigation hidden md:block w-full text-center self-center ${textColorClass}`}>
                            <ul className='navigation__margin flex justify-around items-center'>
                                {menuItems?.map((item, idx) => (
                                    (item?.collection || item?.dropdown || item?.title) ? (
                                        <HeaderLink
                                            index={item.index}
                                            key={idx}
                                            linkItem={item} />
                                    ) : (
                                        null
                                    )
                                ))}
                            </ul>
                        </nav>

                        <div className='hidden md:block'>
                            <a
                                className={`block button h-[36px] ${buttonClass}`}
                                href={`${formatURI('/contact')}`}>
                                <span>{t('Get In Touch')}</span>
                                <span className='button__icon button__icon--animate'>
                                    <IconChevronSprite />
                                </span>
                            </a>
                        </div>

                    </header>

                    <LocaleSelector stylingWhite={stylingWhite} />
                </div>

                {menuItems?.map((link, i) => {
                    if (link?.dropdown) {
                        return (
                            <HeaderDrawer
                                headerOpen={headerOpen === i + 1}
                                key={i}
                                links={link?.collection} />
                        );
                    }
                    return null;
                })?.filter(Boolean)}
            </div>
        </>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    menu: PropTypes.shape({
        link1: PropTypes.object,
        link1DropdownCollection: PropTypes.object,
        link1Title: PropTypes.string,
        link2: PropTypes.object,
        link2DropdownCollection: PropTypes.object,
        link2Title: PropTypes.string,
        link3: PropTypes.object,
        link3DropdownCollection: PropTypes.object,
        link3Title: PropTypes.string,
        link4: PropTypes.object,
        link4DropdownCollection: PropTypes.object,
        link4Title: PropTypes.string,
    }),
    stylingWhite: PropTypes.bool,
    setStylingWhite: PropTypes.func
};

HeaderLanding.propTypes = propTypes;

export default HeaderLanding;
